/**
 * Floating action button — bottom-right, always visible. Opens the help
 * drawer to the current page's contextual article (the first article
 * whose `contexts[]` includes the page's contextKey), or the index if
 * none matches.
 *
 * Hover-preview: when the inspector hovers the FAB on desktop, a small
 * tooltip flips out showing the title of the article that will open.
 * This makes contextual help visible — without the preview, users
 * don't know the button does anything beyond a generic "open help".
 */

import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { HelpCircle, BookOpen } from 'lucide-react';
import { useHelp } from './HelpProvider';
import { articlesForContext } from './articles';

/** Public pages with their own fixed chrome (tour footer, check CTA) —
 *  the FAB overlaps their controls and its articles assume a signed-in
 *  user, so it stays off these routes entirely. */
const HIDDEN_ROUTES = ['/vetting/demo', '/check'];

export default function HelpButton() {
  const { open, contextKey, openHelp } = useHelp();
  const [hover, setHover] = useState(false);
  const { pathname } = useLocation();

  if (HIDDEN_ROUTES.some((r) => pathname.startsWith(r))) return null;

  const matches = articlesForContext(contextKey);
  const targetArticle = matches[0];

  function handleClick() {
    openHelp(targetArticle?.slug ?? null);
  }

  return (
    <div className={`fixed z-30 bottom-4 right-4 sm:bottom-5 sm:right-5 ${open ? 'pointer-events-none' : ''}`}>
      {/* Preview pill */}
      {targetArticle && hover && !open && (
        <div
          className="hidden sm:flex absolute right-full mr-3 top-1/2 -translate-y-1/2 items-center gap-2 px-3 py-2 rounded-lg bg-slate-900/95 border border-cyan-500/30 backdrop-blur-md shadow-xl text-[11px] whitespace-nowrap"
          style={{ boxShadow: '0 12px 30px -10px rgba(0,0,0,0.6)' }}
        >
          <BookOpen className="w-3.5 h-3.5 text-cyan-300 shrink-0" />
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-widest text-cyan-300 font-bold">For this page</div>
            <div className="text-slate-100 font-medium truncate max-w-[40ch]">{targetArticle.title}</div>
          </div>
          {/* Arrow */}
          <span className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-2 h-2 bg-slate-900/95 border-r border-b border-cyan-500/30 rotate-[-45deg]" />
        </div>
      )}

      <button
        onClick={handleClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        aria-label={targetArticle ? `Open help: ${targetArticle.title}` : 'Open help (or press ?)'}
        title={targetArticle ? `Help: ${targetArticle.title} · ?` : 'Help · ?'}
        className={`
          w-12 h-12 rounded-full
          bg-gradient-to-br from-cyan-500 to-blue-600
          hover:from-cyan-400 hover:to-blue-500
          shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50
          text-white flex items-center justify-center
          border border-cyan-400/40
          transition-all duration-150 ease-out active:scale-[0.94] hover:scale-[1.04]
          ${open ? 'opacity-0 scale-90' : 'opacity-100'}
        `}
      >
        <HelpCircle className="w-5 h-5" />
        <span className="sr-only">Help</span>
        {/* Keyboard hint pill on hover (desktop only) */}
        <span className="hidden sm:flex absolute -top-1.5 -right-1.5 px-1.5 py-0.5 rounded-md text-[9px] font-mono bg-slate-950 border border-slate-700 text-cyan-300 shadow">?</span>
      </button>
    </div>
  );
}
